import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// utils
import { telephonyEnabled } from '../../../../utils/telephony';

// components
import MuteButton from './MuteButton';
import CallDuration from './CallDuration';
import EndCall from './EndCall';
import RedialButton from './RedialButton';
import AcceptCall from './AcceptCallButton';

function NoPermissions() {
  return (
    <span className="text-danger fw-bold">
      Your account does not have permissions for calls.
    </span>
  );
}

function NotConnected() {
  const { baseRoute } = useSelector(store => store.form);
  const navigate = useNavigate();
  return (
    <span>
      <span className="me-2">
        Telephony not connected
      </span>
      (
      <u
        role="button"
        tabIndex={0}
        className="link-primary text-underline"
        onClick={() => navigate(`/enroll${baseRoute}/telephony/call`)}
      >
        connect
      </u>
      )
    </span>
  );
}

function Connecting() {
  return (
    <span className="ellipses-loading">
      Connecting to telephony
    </span>
  );
}

function AwaitingCall() {
  return (
    <span>
      Awaiting call from client
    </span>
  );
}

function IncomingCall() {
  return (
    <>
      <AcceptCall />
      <span className="ms-3 ellipses-loading">
        Incoming call
      </span>
    </>
  );
}

function OngoingCall() {
  return (
    <>
      <EndCall className="me-3" />
      <MuteButton className="me-3" />
      <CallDuration className="me-3" />
    </>
  );
}

function Redial() {
  return <RedialButton />;
}

function Dialing() {
  return (
    <>
      <EndCall className="me-3" />
      <span className="ellipses-loading">
        Dialing
      </span>
    </>
  );
}

function LoadingCall() {
  return (
    <span className="ellipses-loading">
      Connecting call
    </span>
  );
}

function EndingCall() {
  return (
    <span className="ellipses-loading">
      Ending call
    </span>
  );
}

function CallStatus() {
  const {
    telephony: { status, ccpAgentState },
    theme: { telephony, inPersonTelephony },
    agent: { mmbPermissions: { can_use_telephony: telephonyPerms } },
  } = useSelector(store => store);

  const te = telephonyEnabled({ telephony, inPersonTelephony });

  let callStatus = <NotConnected />;

  if (!telephonyPerms) {
    callStatus = <NoPermissions />;
  } else if (status === 'acw' && ccpAgentState === 'Available') {
    callStatus = <AwaitingCall />;
  } else if (status === 'noauth' && ccpAgentState === 'Authenticating') {
    callStatus = <Connecting />;
  } else if (status === 'connecting' && ccpAgentState === 'PendingBusy') {
    callStatus = <IncomingCall />;
  } else if (['accepted', 'connected'].includes(status) && ccpAgentState === 'Busy') {
    callStatus = <OngoingCall />;
  } else if (status === 'destroy' && ccpAgentState === 'Available') {
    callStatus = <Redial />;
  } else if (status === 'connecting' && ccpAgentState === 'CallingCustomer') {
    callStatus = <Dialing />;
  } else if (status === 'accepted' && ccpAgentState === 'PendingBusy') {
    callStatus = <LoadingCall />;
  } else if (status === 'ended' && ccpAgentState === 'AfterCallWork') {
    callStatus = <EndingCall />;
  }

  return te && (
    <div className="d-flex align-items-center text-muted">
      {callStatus}
    </div>
  );
}

export default CallStatus;
